import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    CircularProgress,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import { toast } from 'react-toastify';
import { db } from '../../../../../Config/firebase';
import { 
    collection, 
    query, 
    where, 
    getDocs, 
    writeBatch 
} from 'firebase/firestore';
import parsePhoneNumber from 'libphonenumber-js';

interface ChangePhoneNumberModalProps {
    open: boolean;
    onClose: () => void;
    currentPhoneNumber: string;
    studentName: string;
    liveSubscriptionId: string;
    serviceProviderId: string;
    onPhoneNumberChanged: () => void;
}

const ChangePhoneNumberModal: React.FC<ChangePhoneNumberModalProps> = ({
    open,
    onClose,
    currentPhoneNumber,
    studentName,
    liveSubscriptionId,
    serviceProviderId,
    onPhoneNumberChanged
}) => {
    const [newPhoneNumber, setNewPhoneNumber] = useState('');
    const [formattedNewNumber, setFormattedNewNumber] = useState('');
    const [formattedCurrentNumber, setFormattedCurrentNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const [validationError, setValidationError] = useState('');

    useEffect(() => {
        if (open) {
            // Reset form when modal opens
            setNewPhoneNumber('');
            setFormattedNewNumber('');
            setConfirmationChecked(false);
            setValidationError('');
            setLoading(false);

            // Format current phone number for display
            try {
                const parsed = parsePhoneNumber(currentPhoneNumber);
                setFormattedCurrentNumber(parsed?.formatInternational() || currentPhoneNumber);
            } catch {
                setFormattedCurrentNumber(currentPhoneNumber);
            }
        }
    }, [open, currentPhoneNumber]);

    const validatePhoneNumber = (phoneNumber: string) => {
        try {
            const parsed = parsePhoneNumber(phoneNumber);
            if (!parsed || !parsed.isValid()) {
                return { isValid: false, error: 'Please enter a valid phone number with country code (e.g., +91 9876543210)' };
            }
            if (parsed.format('E.164') === currentPhoneNumber) {
                return { isValid: false, error: 'New phone number must be different from the current phone number' };
            }
            return { isValid: true, formatted: parsed.format('E.164'), display: parsed.formatInternational() };
        } catch {
            return { isValid: false, error: 'Please enter a valid phone number with country code (e.g., +91 9876543210)' };
        }
    };

    const handlePhoneNumberChange = (value: string) => {
        setNewPhoneNumber(value);
        setValidationError('');
        
        if (value.trim()) {
            const validation = validatePhoneNumber(value);
            if (validation.isValid) {
                setFormattedNewNumber(validation.display || '');
            } else {
                setValidationError(validation.error);
                setFormattedNewNumber('');
            }
        } else {
            setFormattedNewNumber('');
        }
    };

    const checkNewPhoneNumberAvailability = async (newPhoneNumber: string) => {
        const serviceConsumersRef = collection(db, 'ServiceConsumers');
        const q = query(serviceConsumersRef, where('phone_number', '==', newPhoneNumber));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
            throw new Error(
                `Phone number conflict detected: The new phone number ${newPhoneNumber} is already registered with another ServiceConsumer. ` +
                `This indicates that another user is already using this phone number. Please contact support@hosaksham.com with the following details:\n\n` +
                `- Student Name: ${studentName}\n` +
                `- Current Phone: ${currentPhoneNumber}\n` +
                `- Requested New Phone: ${newPhoneNumber}\n` +
                `- Live Subscription ID: ${liveSubscriptionId}\n` +
                `- Existing ServiceConsumers with new number: ${querySnapshot.size}\n\n` +
                `Please choose a different phone number or contact support to resolve this conflict.`
            );
        }
    };

    const updateServiceConsumer = async (oldPhoneNumber: string, newPhoneNumber: string) => {
        const serviceConsumersRef = collection(db, 'ServiceConsumers');
        const q = query(serviceConsumersRef, where('phone_number', '==', oldPhoneNumber));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
            throw new Error('No ServiceConsumer found with the current phone number');
        }

        // Check for data integrity issue - should only be one ServiceConsumer per phone number
        if (querySnapshot.size > 1) {
            throw new Error(
                `Data integrity issue detected: Found ${querySnapshot.size} ServiceConsumers with the same phone number. ` +
                `This indicates a database inconsistency. Please contact support@hosaksham.com with the following details:\n\n` +
                `- Student Name: ${studentName}\n` +
                `- Current Phone: ${oldPhoneNumber}\n` +
                `- New Phone: ${newPhoneNumber}\n` +
                `- Live Subscription ID: ${liveSubscriptionId}\n` +
                `- Number of duplicate ServiceConsumers: ${querySnapshot.size}\n\n` +
                `Please do not attempt to change the phone number until this issue is resolved.`
            );
        }

        // If exactly one ServiceConsumer found, proceed with update
        const batch = writeBatch(db);
        querySnapshot.forEach((doc) => {
            batch.update(doc.ref, { phone_number: newPhoneNumber });
        });

        await batch.commit();
        return querySnapshot.size;
    };

    const updateLiveSubscriptions = async (oldPhoneNumber: string, newPhoneNumber: string, serviceProviderId: string) => {
        const liveSubsRef = collection(db, 'ServiceProviders', serviceProviderId, 'LiveSubscriptions');
        const q = query(liveSubsRef, where('phoneNumber', '==', oldPhoneNumber));
        const querySnapshot = await getDocs(q);
        
        const batch = writeBatch(db);
        
        querySnapshot.forEach((doc) => {
            batch.update(doc.ref, { phoneNumber: newPhoneNumber });
        });

        if (querySnapshot.size > 0) {
            await batch.commit();
        }
        
        return querySnapshot.size;
    };

    const handleSubmit = async () => {
        const validation = validatePhoneNumber(newPhoneNumber);
        if (!validation.isValid) {
            setValidationError(validation.error);
            return;
        }

        if (!confirmationChecked) {
            toast.error('Please confirm that you understand the implications of changing the phone number');
            return;
        }

        setLoading(true);
        try {
            const newFormattedNumber = validation.formatted!;

            // Step 1: Check if new phone number is already taken
            await checkNewPhoneNumberAvailability(newFormattedNumber);

            // Step 2: Update ServiceConsumers
            const serviceConsumerCount = await updateServiceConsumer(currentPhoneNumber, newFormattedNumber);
            
            // Step 3: Update all LiveSubscriptions for this service provider
            const liveSubCount = await updateLiveSubscriptions(currentPhoneNumber, newFormattedNumber, serviceProviderId);

            toast.success(
                `Phone number updated successfully! ` +
                `Updated ${serviceConsumerCount} ServiceConsumer(s) and ${liveSubCount} LiveSubscription(s). Refreshing data...`
            );

            // Call the callback to refresh data
            onPhoneNumberChanged();
            onClose();

        } catch (error) {
            console.error('Error updating phone number:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            
            // For critical issues, show a more prominent error
            if (errorMessage.includes('Data integrity issue detected') || errorMessage.includes('Phone number conflict detected')) {
                toast.error(errorMessage, {
                    autoClose: false, // Don't auto-close for important errors
                    style: { whiteSpace: 'pre-line' } // Preserve line breaks
                });
            } else {
                toast.error(`Failed to update phone number: ${errorMessage}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm" 
            fullWidth
            disableEscapeKeyDown={loading}
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(0,0,0,0.05)'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box className="flex items-center gap-3 mb-2">
                    <Box className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                        <span className="text-white text-lg">📱</span>
                    </Box>
                    <Box>
                        <Typography variant="h6" className="font-semibold text-gray-800">
                            Change Phone Number
                        </Typography>
                        <Typography variant="body2" className="text-gray-500 mt-0.5">
                            Update student contact information
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: 3, pb: 2 }}>
                <Box className="space-y-5">
                    {/* Student Info */}
                    <Box className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100 shadow-sm">
                        <Box className="flex items-center gap-2 mb-3">
                            <Box className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center">
                                <span className="text-white text-xs">👤</span>
                            </Box>
                            <Typography variant="subtitle1" className="text-blue-800 font-semibold">
                                Student Information
                            </Typography>
                        </Box>
                        <Box className="space-y-2">
                            <Box className="flex items-center gap-2">
                                <Typography variant="body2" className="text-gray-600 min-w-[100px]">Name:</Typography>
                                <Typography variant="body2" className="font-medium text-gray-800">{studentName}</Typography>
                            </Box>
                            <Box className="flex items-center gap-2">
                                <Typography variant="body2" className="text-gray-600 min-w-[100px]">Current Phone:</Typography>
                                <Typography variant="body2" className="font-medium text-gray-800 font-mono bg-white px-2 py-1 rounded border">{formattedCurrentNumber}</Typography>
                            </Box>
                            <Box className="flex items-center gap-2">
                                <Typography variant="body2" className="text-gray-600 min-w-[100px]">Subscription ID:</Typography>
                                <Typography variant="body2" className="font-medium text-gray-800 font-mono text-xs bg-gray-100 px-2 py-1 rounded">{liveSubscriptionId}</Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Warning Alert */}
                    <Box className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-xl p-4 shadow-sm">
                        <Box className="flex items-center gap-2 mb-3">
                            <Box className="w-6 h-6 bg-orange-500 rounded-lg flex items-center justify-center">
                                <span className="text-white text-xs">⚠️</span>
                            </Box>
                            <Typography variant="subtitle2" className="font-semibold text-orange-800">
                                Important: What will happen when you change the phone number?
                            </Typography>
                        </Box>
                        <Box className="space-y-2 text-sm text-orange-700">
                            <Box className="flex items-start gap-2">
                                <span className="text-orange-500 mt-0.5">•</span>
                                <span>The student will <strong>no longer be able to login</strong> to the Student Dashboard using the old phone number</span>
                            </Box>


                            <Box className="flex items-start gap-2">
                                <span className="text-orange-500 mt-0.5">•</span>
                                <span>Existing unpaid orders will not be updated with the new phone number</span>

                            </Box>
                            <Box className="flex items-start gap-2">
                                <span className="text-orange-500 mt-0.5">•</span>
                                <span>The student will need to use the <strong>new phone number</strong> for future logins</span>
                            </Box>
                            <Box className="flex items-start gap-2">
                                <span className="text-orange-500 mt-0.5">•</span>
                                <span>This action <strong>cannot be easily undone</strong></span>
                            </Box>
                        </Box>
                    </Box>

                    {/* Data Integrity Info */}
                    <Box className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4 shadow-sm">
                        <Box className="flex items-center gap-2 mb-2">
                            <Box className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center">
                                <span className="text-white text-xs">🔍</span>
                            </Box>
                            <Typography variant="subtitle2" className="font-semibold text-blue-800">
                                Data Integrity Check
                            </Typography>
                        </Box>
                        <Typography variant="body2" className="text-blue-700">
                            If multiple ServiceConsumers are found with the same phone number, the operation will be blocked and you'll need to contact <strong>support@hosaksham.com</strong> to resolve the data inconsistency before proceeding.
                        </Typography>
                    </Box>

                    {/* Phone Number Input */}
                    <Box>
                        <Box className="flex items-center gap-2 mb-3">
                            <Box className="w-6 h-6 bg-green-500 rounded-lg flex items-center justify-center">
                                <span className="text-white text-xs">📞</span>
                            </Box>
                            <Typography variant="subtitle1" className="font-semibold text-gray-800">
                                New Phone Number
                            </Typography>
                        </Box>
                        <TextField
                            fullWidth
                            value={newPhoneNumber}
                            onChange={(e) => handlePhoneNumberChange(e.target.value)}
                            placeholder="Enter new phone number with country code (e.g., +91 9876543210)"
                            error={!!validationError}
                            helperText={validationError || 'Include country code (e.g., +91 for India)'}
                            disabled={loading}
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    fontFamily: 'monospace',
                                    '&:hover fieldset': {
                                        borderColor: '#3B82F6',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#3B82F6',
                                        borderWidth: '2px',
                                    }
                                }
                            }}
                        />
                        {formattedNewNumber && (
                            <Box className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                                <Box className="flex items-center gap-2">
                                    <span className="text-green-600 text-lg">✓</span>
                                    <Typography variant="body2" className="text-green-700 font-medium">
                                        Formatted: <span className="font-mono bg-white px-2 py-1 rounded border">{formattedNewNumber}</span>
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {/* Confirmation Checkbox */}
                    <Box className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={confirmationChecked}
                                    onChange={(e) => setConfirmationChecked(e.target.checked)}
                                    disabled={loading}
                                    color="primary"
                                    sx={{
                                        '&.Mui-checked': {
                                            color: '#10B981',
                                        }
                                    }}
                                />
                            }
                            label={
                                <Box className="ml-2">
                                    <Typography variant="body2" className="text-gray-700 leading-relaxed">
                                        I understand that the student will not be able to login with the old phone number 
                                        <span className="font-mono bg-white px-1 py-0.5 rounded mx-1 text-sm border">{formattedCurrentNumber}</span> 
                                        and will need to use the new phone number for future access to the Student Dashboard.
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 2, gap: 2 }}>
                <Button 
                    onClick={handleClose} 
                    disabled={loading}
                    variant="outlined"
                    sx={{
                        borderRadius: '12px',
                        px: 3,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: '#E5E7EB',
                        color: '#6B7280',
                        '&:hover': {
                            borderColor: '#D1D5DB',
                            backgroundColor: '#F9FAFB'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit}
                    disabled={loading || !formattedNewNumber || !confirmationChecked}
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{
                        borderRadius: '12px',
                        px: 4,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        background: loading ? '#9CA3AF' : 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
                        '&:hover': {
                            background: loading ? '#9CA3AF' : 'linear-gradient(135deg, #2563EB, #1E40AF)',
                        },
                        '&:disabled': {
                            background: '#E5E7EB',
                            color: '#9CA3AF'
                        },
                        boxShadow: loading ? 'none' : '0 4px 12px rgba(59, 130, 246, 0.4)',
                    }}
                >
                    {loading ? 'Updating...' : 'Update Phone Number'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangePhoneNumberModal;
